var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef } from 'react';
import '@cb/apricot/CBButton';
import { glyphNames } from '@cb/apricot-react-glyph';
import { iconNames } from '@cb/apricot-react-icon';
import cn from 'classnames';
export const buttonVariants = [
    'primary',
    'naked',
    'tag',
    'circle',
    'square',
    'black',
    'yellow',
    'card-primary',
    'card-secondary',
];
const LoadingDots = () => (React.createElement("div", { className: "cb-btn-pulse-dots" },
    React.createElement("div", { className: "cb-btn-dots" })));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isIcon = (x) => iconNames.includes(x);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isGlyph = (x) => glyphNames.includes(x);
export const BaseButton = React.forwardRef((_a, ref) => {
    var { children, icon, iconLeft, iconDecorative = true, variant, disabled, small, large, className, withAriaLive, loading, light, hoverShade, floating, greyscale, noPadding, noOutline } = _a, props = __rest(_a, ["children", "icon", "iconLeft", "iconDecorative", "variant", "disabled", "small", "large", "className", "withAriaLive", "loading", "light", "hoverShade", "floating", "greyscale", "noPadding", "noOutline"]);
    const btnClassName = cn('cb-btn', variant && `cb-btn-${variant}`, light && 'cb-btn-light', small && 'cb-btn-sm', floating && 'cb-btn-floating', greyscale && 'cb-btn-greyscale', className, loading && 'cb-btn-pulse', noPadding && 'cb-no-padding', noOutline && 'cb-no-outline', hoverShade && 'cb-hover-shade', large && (!variant || ['primary', 'black'].includes(variant)) && 'cb-btn-lg');
    const iconOnly = variant === 'circle' || variant === 'square';
    const iconClassName = cn(icon && isIcon(icon) && 'cb-icon', icon && !isIcon(icon) && isGlyph(icon) && 'cb-glyph', `cb-${icon}`, iconLeft && 'cb-no-margin-left cb-margin-right-8');
    return (React.createElement("button", Object.assign({ type: "button", className: btnClassName, disabled: disabled, "data-cbtrack-comp": "apricot-react:button" }, props, { ref: ref }),
        icon && iconLeft && React.createElement("span", { className: iconClassName, "aria-hidden": iconDecorative }),
        !iconOnly && !loading && children,
        React.createElement("span", { "aria-live": withAriaLive ? 'polite' : undefined }, loading && `Loading`),
        icon && !iconLeft && React.createElement("span", { className: iconClassName, "aria-hidden": iconDecorative }),
        iconOnly && !loading && React.createElement("span", { className: "sr-only" }, children),
        loading && React.createElement(LoadingDots, null)));
});
export const LinkTagButton = React.forwardRef((_a, ref) => {
    var { children, icon, offScreen, iconLeft, iconDecorative = false, variant, disabled, small, className } = _a, props = __rest(_a, ["children", "icon", "offScreen", "iconLeft", "iconDecorative", "variant", "disabled", "small", "className"]);
    const btnRef = useRef(null);
    const btnClassName = cn('cb-btn', variant && `cb-btn-${variant}`, disabled && 'cb-disabled', small && 'cb-btn-sm', className);
    const iconClassName = cn(icon && isIcon(icon) && 'cb-icon', icon && !isIcon(icon) && isGlyph(icon) && 'cb-glyph', `cb-${icon}`, iconLeft && 'cb-no-margin-left cb-margin-right-8');
    const a11YKeydown = (e, node) => {
        const k = e.which || e.keyCode;
        // space or enter
        if (k === 32) {
            e.preventDefault();
            node && node.current.click();
        }
    };
    return (React.createElement("a", Object.assign({ href: "/#", role: "button", className: btnClassName, "aria-disabled": disabled, "data-cbtrack-comp": "apricot-react:button" }, props, { ref: ref || btnRef, onKeyDown: e => a11YKeydown(e, ref || btnRef) }),
        icon && iconLeft && (React.createElement("span", { className: iconClassName, "aria-hidden": iconDecorative }, offScreen && React.createElement("span", { className: "sr-only" }, offScreen))),
        children,
        icon && !iconLeft && (React.createElement("span", { className: iconClassName, "aria-hidden": iconDecorative }, offScreen && React.createElement("span", { className: "sr-only" }, offScreen)))));
});
export const CircularButton = React.forwardRef((_a, ref) => {
    var { label } = _a, props = __rest(_a, ["label"]);
    return (React.createElement(BaseButton, Object.assign({ ref: ref, variant: "circle" }, props), label));
});
export const SquareButton = React.forwardRef((_a, ref) => {
    var { label } = _a, props = __rest(_a, ["label"]);
    return (React.createElement(BaseButton, Object.assign({ ref: ref, variant: "square" }, props), label));
});
export const CloseButton = React.forwardRef((_a, ref) => {
    var { small, label = 'Close', greyscale = true, className } = _a, props = __rest(_a, ["small", "label", "greyscale", "className"]);
    return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    React.createElement(SquareButton, Object.assign({ greyscale: greyscale, icon: "x-mark", label: label, className: cn('cb-btn-close', className), small: small, ref: ref }, props)));
});
export const NakedButton = React.forwardRef((props, ref) => (React.createElement(BaseButton, Object.assign({ variant: "naked", ref: ref }, props))));
export const Tag = (_a) => {
    var { offScreen } = _a, props = __rest(_a, ["offScreen"]);
    return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    React.createElement(React.Fragment, null, props.icon ? (React.createElement(LinkTagButton, Object.assign({ variant: "tag", offScreen: offScreen }, props))) : (React.createElement(BaseButton, Object.assign({ variant: "tag" }, props)))));
};
export const Button = React.forwardRef((props, ref) => (React.createElement(BaseButton, Object.assign({ ref: ref }, props))));
export const PrimaryButton = React.forwardRef((props, ref) => (React.createElement(BaseButton, Object.assign({ variant: "primary", ref: ref }, props))));
export const YellowButton = React.forwardRef((props, ref) => (React.createElement(BaseButton, Object.assign({ variant: "yellow", ref: ref }, props))));
export const SecondaryButton = React.forwardRef((props, ref) => (React.createElement(BaseButton, Object.assign({ variant: "black", ref: ref }, props))));
export const BlackButton = React.forwardRef((props, ref) => (React.createElement(BaseButton, Object.assign({ variant: "black", ref: ref }, props))));
// Will be removed in next major release
export const PrimaryCardButton = React.forwardRef((props, ref) => (React.createElement(BaseButton, Object.assign({ variant: "card-primary", ref: ref }, props))));
// Will be removed in next major release
export const SecondaryCardButton = React.forwardRef((props, ref) => (React.createElement(BaseButton, Object.assign({ variant: "card-secondary", ref: ref }, props))));
export const ButtonRow = ({ small, align, children }) => (React.createElement("div", { className: cn('cb-btn-row', small && 'cb-btn-row-sm', align && `display-flex justify-content-${align}`) }, children));
//# sourceMappingURL=Button.js.map